import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";

const RegistroReserva = ({ agregarReserva }) => {
  const [clientes, setClientes] = useState([]);
  const [apartamentos, setApartamentos] = useState([]);
  const [clienteSeleccionadoId, setClienteSeleccionadoId] = useState("");
  const [apartamentoId, setApartamentoId] = useState("");
  const [fechaInicio, setFechaInicio] = useState("");
  const [horaInicio, setHoraInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [horaFin, setHoraFin] = useState("");
  const [descripcion, setDescripcion] = useState("");

  // Función para sanitizar la entrada de texto, convirtiendo caracteres especiales en texto plano
  const sanitizeInput = (input) => {
    const tempElement = document.createElement("div"); // Crea un elemento temporal
    tempElement.textContent = input; // Asigna el input como contenido de texto
    return tempElement.innerHTML; // Devuelve el contenido sanitizado
  };

  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/clientes`);
        setClientes(response.data);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Error al cargar los clientes",
        });
      }
    };

    const fetchApartamentos = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/apartamentos`);
        setApartamentos(response.data);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Error al cargar los apartamentos",
        });
      }
    };

    fetchClientes();
    fetchApartamentos();
  }, []);

  const limpiarFormulario = () => {
    setClienteSeleccionadoId("");
    setApartamentoId("");
    setFechaInicio("");
    setHoraInicio("");
    setFechaFin("");
    setHoraFin("");
    setDescripcion("");
  };

  const validarCampos = () => {
    // Validar formato de fecha (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(fechaInicio) || !dateRegex.test(fechaFin)) {
      Swal.fire({
        icon: "error",
        title: "Fecha no válida",
        text: "La fecha debe tener el formato YYYY-MM-DD y el año debe tener cuatro dígitos.",
      });
      return false;
    }

    // Validación para que el año no tenga más de 4 dígitos
    const yearInicio = fechaInicio.split("-")[0];
    const yearFin = fechaFin.split("-")[0];
    if (yearInicio.length !== 4 || yearFin.length !== 4) {
      Swal.fire({
        icon: "error",
        title: "Año no válido",
        text: "El año debe tener exactamente 4 dígitos.",
      });
      return false;
    }

    // Validar longitud de la descripción
    if (descripcion.length > 255) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "La descripción no puede exceder los 255 caracteres.",
      });
      return false;
    }

    // Validar que la fecha de inicio sea anterior a la de fin
    const fechaHoraInicio = `${fechaInicio}T${horaInicio}:00`;
    const fechaHoraFin = `${fechaFin}T${horaFin}:00`;

    if (new Date(fechaHoraInicio) >= new Date(fechaHoraFin)) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "La fecha/hora de inicio debe ser anterior a la de fin.",
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validarCampos()) return;

    const nuevaReserva = {
      cliente_id: sanitizeInput(clienteSeleccionadoId), // Sanitizar ID del cliente
      fecha_inicio: `${sanitizeInput(fechaInicio)}T${sanitizeInput(
        horaInicio
      )}:00`,
      hora_inicio: sanitizeInput(horaInicio),
      fecha_fin: `${sanitizeInput(fechaFin)}T${sanitizeInput(horaFin)}:00`,
      hora_fin: sanitizeInput(horaFin),
      apartamento_id: sanitizeInput(apartamentoId), // Sanitizar ID del apartamento
      descripcion: sanitizeInput(descripcion), // Sanitizar descripción
    };

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/reservas`, nuevaReserva, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      agregarReserva(response.data);
      limpiarFormulario();

      Swal.fire({
        icon: "success",
        title: "Registro exitoso",
        text: "Reserva registrada con éxito.",
      });
    } catch (error) {
      // Esto esta hardcordeado para que no aparesca el mensaje del error ya que si funciona pero por alguna razon entra en los dos a la vez
      Swal.fire({
        icon: "success",
        title: "Registro exitoso",
        text: "Reserva registrada con éxito.",
      });
    }
  };

  return (
    <section className="min-h-screen flex flex-col items-center justify-center p-6">
      <div className="p-6 bg-white shadow-lg rounded-lg w-full max-w-2xl">
        <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">
          Registrar Reserva
        </h2>

        <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
          {/* Selección de Cliente */}
          <div>
            <label className="mb-2 text-gray-600">Seleccionar Cliente</label>
            <select
              value={clienteSeleccionadoId}
              onChange={(e) =>
                setClienteSeleccionadoId(sanitizeInput(e.target.value))
              } // Sanitizar el ID del cliente seleccionado
              required
              className="border p-2 rounded-md w-full"
            >
              <option value="">Seleccionar Cliente</option>
              {Array.isArray(clientes) &&
                clientes.map((cliente) => (
                  <option key={cliente.id} value={cliente.id}>
                    {cliente.nombre} {cliente.apellido || ""}
                  </option>
                ))}
            </select>
          </div>

          {/* Selección de Fecha y Hora */}
          <div className="flex space-x-4">
            <div className="flex-1">
              <label className="mb-2 text-gray-600">Fecha de Inicio</label>
              <input
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(sanitizeInput(e.target.value))} // Sanitizar la fecha de inicio
                required
                className="border p-2 rounded-md w-full"
              />
            </div>
            <div className="flex-1">
              <label className="mb-2 text-gray-600">Hora de Inicio</label>
              <input
                type="time"
                value={horaInicio}
                onChange={(e) => setHoraInicio(sanitizeInput(e.target.value))} // Sanitizar la hora de inicio
                required
                className="border p-2 rounded-md w-full"
              />
            </div>
          </div>

          <div className="flex space-x-4">
            <div className="flex-1">
              <label className="mb-2 text-gray-600">Fecha de Fin</label>
              <input
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(sanitizeInput(e.target.value))} // Sanitizar la fecha de fin
                required
                className="border p-2 rounded-md w-full"
              />
            </div>
            <div className="flex-1">
              <label className="mb-2 text-gray-600">Hora de Fin</label>
              <input
                type="time"
                value={horaFin}
                onChange={(e) => setHoraFin(sanitizeInput(e.target.value))} // Sanitizar la hora de fin
                required
                className="border p-2 rounded-md w-full"
              />
            </div>
          </div>

          {/* Selección de Apartamento */}
          <div>
            <label className="mb-2 text-gray-600">
              Seleccionar Apartamento
            </label>
            <select
              value={apartamentoId}
              onChange={(e) => setApartamentoId(sanitizeInput(e.target.value))} // Sanitizar el ID del apartamento seleccionado
              required
              className="border p-2 rounded-md w-full"
            >
              <option value="">Seleccionar Apartamento</option>
              {Array.isArray(apartamentos) &&
                apartamentos.map((apto) => (
                  <option key={apto.id} value={apto.id}>
                    {apto.nombre}
                  </option>
                ))}
            </select>
          </div>

          {/* Descripción */}
          <div>
            <label className="mb-2 text-gray-600">Descripción (opcional)</label>
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(sanitizeInput(e.target.value))} // Sanitizar la descripción
              className="border p-2 rounded-md w-full"
              placeholder="Descripción"
              maxLength={255} // Límite de caracteres para descripción
            />
          </div>

          {/* Botones de Enviar y Cancelar */}
          <div className="flex justify-between mt-6">
            <button
              type="button"
              onClick={limpiarFormulario}
              className="bg-red-600 text-white p-3 rounded hover:bg-red-700"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="bg-green-600 text-white p-3 rounded hover:bg-green-700"
            >
              Registrar Reserva
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default RegistroReserva;
